import type { FirebasePerformance, PerformanceTrace } from 'firebase/performance';
import { getFirebasePublicConfig } from './config';
import { isFirebasePerformanceEnabled } from './performance-config';

export type PerformanceMeasurement = {
  name: string;
  startTime: number;
  duration: number;
  attributes: Record<string, string>;
  metrics?: Record<string, number>;
};

// A slow/blocked SDK import must not retain Axios configs, responses or promises
// per request. Initial measurements are primitive-only and bounded.
const MAX_PENDING_MEASUREMENTS = 64;
let pending: { measurement: PerformanceMeasurement; generation: number }[] = [];
let desiredConsent = false;
let consentGeneration = 0;
let performanceInstance: FirebasePerformance | null = null;
let createTrace: ((performance: FirebasePerformance, name: string) => PerformanceTrace) | null = null;
let initialization: Promise<void> | null = null;
let initializationFailed = false;

export const getPerformanceConsentGeneration = (): number | null =>
  desiredConsent && isFirebasePerformanceEnabled() && !window.location.hash
    ? consentGeneration
    : null;

export const setFirebasePerformanceConsent = (granted: boolean): void => {
  if (desiredConsent !== granted) {
    consentGeneration += 1;
  }
  desiredConsent = granted;
  if (getPerformanceConsentGeneration() === null) {
    pending = [];
  }
  if (performanceInstance) {
    performanceInstance.instrumentationEnabled = false;
    performanceInstance.dataCollectionEnabled = false;
  }
};

const emit = (measurement: PerformanceMeasurement, generation: number): void => {
  if (
    !performanceInstance ||
    !createTrace ||
    generation !== getPerformanceConsentGeneration()
  ) {
    return;
  }

  try {
    // The SDK also treats arbitrary performance.measure() entries as custom
    // traces. Keep custom collection off except during this synchronous call;
    // observer callbacks cannot interleave with this stack.
    performanceInstance.dataCollectionEnabled = true;
    createTrace(performanceInstance, measurement.name).record(
      measurement.startTime,
      measurement.duration,
      { attributes: measurement.attributes, metrics: measurement.metrics }
    );
  } catch {
    // Optional monitoring cannot change an API response or interrupt rendering.
  } finally {
    performanceInstance.dataCollectionEnabled = false;
  }
};

export const recordPerformanceTrace = (
  measurement: PerformanceMeasurement,
  generation: number
): void => {
  if (
    generation !== getPerformanceConsentGeneration() ||
    !Number.isFinite(measurement.startTime) ||
    measurement.startTime <= 0 ||
    !Number.isFinite(measurement.duration) ||
    measurement.duration <= 0
  ) {
    return;
  }

  if (performanceInstance) {
    emit(measurement, generation);
  } else if (!initializationFailed && pending.length < MAX_PENDING_MEASUREMENTS) {
    pending.push({ measurement, generation });
  }
};

// Called after window load, when the browser is idle. Nothing on the request
// path waits for this promise, and a failed initialization is not retried.
export const initializeFirebasePerformance = (): Promise<void> => {
  if (getPerformanceConsentGeneration() === null) {
    return Promise.resolve();
  }
  if (initialization) {
    return initialization;
  }

  initialization = Promise.all([import('firebase/app'), import('firebase/performance')])
    .then(([appModule, performanceModule]) => {
      const config = getFirebasePublicConfig();
      if (!config || getPerformanceConsentGeneration() === null) {
        pending = [];
        initialization = null;
        return;
      }

      const existingApp = appModule.getApps().find(app => app.name === '[DEFAULT]');
      if (
        existingApp &&
        (existingApp.options.appId !== config.appId || existingApp.options.projectId !== config.projectId)
      ) {
        pending = [];
        initializationFailed = true;
        return;
      }
      const app = existingApp ?? appModule.initializeApp(config);
      performanceInstance = performanceModule.initializePerformance(app, {
        // Only our allowlisted, fixed-name measurements are collected. The SDK's
        // automatic resource sweep includes unrelated URLs and pre-consent work.
        instrumentationEnabled: false,
        dataCollectionEnabled: false,
      });
      createTrace = performanceModule.trace;
      const buffered = pending;
      pending = [];
      buffered.forEach(({ measurement, generation }) => emit(measurement, generation));
    })
    .catch(() => {
      pending = [];
      initializationFailed = true;
    });
  return initialization;
};
